/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import React, { useMemo, useState } from 'react';

import styled from 'styled-components';

import useProfileSizes from '~/pdp/utils/useProfileSizes';
import { getIsPlusProfile } from '~/shared/utils/getIsPlusProfile';
import { Link } from '~/techstyle-shared/next-routes';
import { ProductType } from '~/techstyle-shared/react-products';

import { MAX_CARD_WIDTH } from '../../constants';
import useGridInputs from '../../hooks/useGridInputs';
import useProductCardTracking from '../../hooks/useProductCardTracking';
import useProductGridData from '../../hooks/useProductGridData';
import {
  getActiveProduct,
  getSwatchesWithPdpLink,
  shouldShowPlusSizeImageForProductCard,
} from '../../utils/activeProduct';

import ProductCardCallout from './ProductCardCallout';
import ProductCardCalloutOverlay from './ProductCardCalloutOverlay';
import ProductCardFavoriteButton from './ProductCardFavoriteButton';
import ProductCardImage from './ProductCardImage';
import ProductCardImageLockedTreatmentWrapper from './ProductCardImageLockedTreatmentWrapper';
import ProductCardPricing from './ProductCardPricing';
import ProductCardSwatches from './ProductCardSwatches';
import ProductName from './ProductName';

const TrackedLink = styled.a`
  display: block;
  width: 100%;
  position: relative;
`;

const ProductCardContainer = styled.div`
  position: relative;
  max-width: ${MAX_CARD_WIDTH}px;
`;

const ProductCellWrapper = styled.div`
  margin: 8px 0;
`;

const ProductCard = ({
  id,
  label,
  permalink,
  image_service_url,
  item_number,
  pricing,
  relatedProductsData,
  sku,
  facets,
  tag_id_list = [],
  membership_brand_id,
  product_type_id,
  token_redemption_quantity,
  featured_product_location_id_list,
  product_category_id_list,
  active_featured_product_location_code_list,
  active_featured_product_location_id_list,
  date_expected,
  available_quantity_master,
  review_count,
  default_product_category_label,
  average_review,
  gridIndex,
  color,
  alias,
  is_token,
}) => {
  const [activeProductId, setActiveProductId] = useState(Number(id));
  const { productSource } = useGridInputs();
  const { selectedUrlFilters } = useProductGridData();

  // TODO: add isClearanceItem Logic to ProductName?
  // isClearanceItem={isClearanceItem}
  // TODO: ProductName - question re: prefix and suffix (see below story for context)
  // src/techstyle-shared/react-components/src/ProductName/ProductName.stories.js

  const { profileSizes } = useProfileSizes({ isPdp: false });
  const isPlusProfile = getIsPlusProfile(profileSizes);
  const showPlusSizeImage = shouldShowPlusSizeImageForProductCard({
    productFacets: facets,
    selectedUrlFilters,
    isPlusProfile,
  });

  const cioProductData = useMemo(
    () => ({
      id,
      label,
      permalink,
      image_service_url,
      item_number,
      pricing,
      sku,
      facets,
      tag_id_list,
      membership_brand_id,
      product_type_id,
      token_redemption_quantity,
      featured_product_location_id_list,
      product_category_id_list,
      active_featured_product_location_code_list,
      active_featured_product_location_id_list,
      date_expected,
      available_quantity_master,
      review_count,
      default_product_category_label,
      average_review,
      color,
      alias,
      is_token,
    }),
    [
      active_featured_product_location_code_list,
      active_featured_product_location_id_list,
      alias,
      available_quantity_master,
      average_review,
      color,
      date_expected,
      default_product_category_label,
      facets,
      featured_product_location_id_list,
      id,
      image_service_url,
      is_token,
      item_number,
      label,
      membership_brand_id,
      permalink,
      pricing,
      product_category_id_list,
      product_type_id,
      review_count,
      sku,
      tag_id_list,
      token_redemption_quantity,
    ]
  );

  const activeProduct = useMemo(
    () =>
      getActiveProduct({
        relatedProducts: relatedProductsData?.relatedProducts,
        swatches: relatedProductsData?.swatches,
        cioProductData,
        productSource,
        activeProductId,
        relatedProductsData,
      }),
    [relatedProductsData, cioProductData, productSource, activeProductId]
  );

  const swatchesWithPdpLink = useMemo(
    () =>
      getSwatchesWithPdpLink({
        swatches: relatedProductsData?.swatches,
        productSource,
        cioProductData,
        relatedProducts: relatedProductsData?.relatedProducts,
      }),
    [relatedProductsData, cioProductData, productSource]
  );

  const { trackProductClick, productCardRef, trackingTags } =
    useProductCardTracking({
      activeProduct,
      productSource,
      gridIndex,
    });

  // Bundles do not have swatch images. The swatch urls may exist from the APIs (or are manually constructed),
  // but the image binary is not available when trying to fetch from the CDN.
  // If the primary product is a bundle, we do not show the swatches, even for related products
  const showProductCardSwatches = product_type_id !== ProductType.BUNDLE;

  return (
    <ProductCardContainer ref={productCardRef} {...trackingTags}>
      <ProductCardFavoriteButton product={activeProduct} />
      <Link href={activeProduct?.pdpLink}>
        <TrackedLink onClick={trackProductClick}>
          <ProductCardImageLockedTreatmentWrapper
            tagIdList={activeProduct?.tagIdList}
          >
            <ProductCardImage
              imageServiceUrl={activeProduct?.imageServiceUrl}
              swatchLabel={activeProduct?.swatchLabel}
              images={activeProduct?.images}
              sku={activeProduct?.sku}
              isPrimaryProduct={Number(activeProduct?.id) === Number(id)}
              showPlusSizeImage={showPlusSizeImage}
              permalink={activeProduct?.permalink}
            />
          </ProductCardImageLockedTreatmentWrapper>
          <ProductCardCalloutOverlay product={activeProduct} />
        </TrackedLink>
      </Link>
      <ProductCellWrapper>
        {showProductCardSwatches && (
          <ProductCardSwatches
            swatches={swatchesWithPdpLink}
            activeProductId={activeProductId}
            setActiveProductId={setActiveProductId}
            activeProductPdpLink={activeProduct?.pdpLink}
          />
        )}
        <Link href={activeProduct?.pdpLink}>
          <TrackedLink onClick={trackProductClick}>
            <ProductName>
              {activeProduct?.label.replace(/&amp;/g, '&')}
            </ProductName>
            <ProductCardPricing product={activeProduct} />
            <ProductCardCallout product={activeProduct} />
          </TrackedLink>
        </Link>
      </ProductCellWrapper>
    </ProductCardContainer>
  );
};

export default ProductCard;
